import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronRight, Home } from "lucide-react";
import {
  breadcrumbListSchema,
  breadcrumbTrail,
  isBreadcrumbHidden,
} from "@/lib/breadcrumbTrail";

/**
 * Pull a nicer label for dynamic routes (blog posts, etc.) out of the deepest
 * match's loader data when one is available.
 */
function labelFromLoaderData(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const d = data as Record<string, unknown>;
  const candidates = [
    d["breadcrumbLabel"],
    (d["post"] as Record<string, unknown> | undefined)?.["title"],
    d["title"],
  ];
  const found = candidates.find((c) => typeof c === "string" && c.trim().length > 0);
  return typeof found === "string" ? found : undefined;
}

/**
 * Site-wide breadcrumbs. Rendered once from the root layout so every public
 * page gets a consistent visible trail plus matching BreadcrumbList JSON-LD.
 */
export function Breadcrumbs() {
  const { pathname, lastLabel } = useRouterState({
    select: (s) => {
      const matches = s.matches;
      const deepest = matches[matches.length - 1];
      return {
        pathname: s.location.pathname,
        lastLabel: labelFromLoaderData(deepest?.loaderData),
      };
    },
  });

  if (isBreadcrumbHidden(pathname)) return null;

  const crumbs = breadcrumbTrail(pathname, lastLabel);
  if (crumbs.length < 2) return null;

  return (
    <nav aria-label="Breadcrumb" className="border-b border-border/60 bg-card/20">
      <div className="container mx-auto px-6 py-3.5">
        <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {crumbs.map((crumb, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <li key={`${crumb.name}-${i}`} className="flex items-center gap-2">
                {i > 0 && (
                  <ChevronRight aria-hidden="true" className="h-3.5 w-3.5 shrink-0 opacity-50" />
                )}
                {isLast || !crumb.path ? (
                  <span aria-current="page" className="font-medium text-foreground">
                    {crumb.name}
                  </span>
                ) : (
                  <Link
                    to={crumb.path}
                    className="inline-flex items-center gap-1.5 transition-colors hover:text-brand-text"
                  >
                    {i === 0 && <Home aria-hidden="true" className="h-3.5 w-3.5" />}
                    {crumb.name}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbListSchema(crumbs, pathname)),
        }}
      />
    </nav>
  );
}
